import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { addPartnership, addPartnershipUserInvitation, uploadPartnershipLogo, Timestamp } from "@/lib/firebase";
import { Loader2, Handshake, Mail, Upload, X } from "lucide-react";
import { logger } from "@/lib/logger";

interface AddPartnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AddPartnershipModal({
  isOpen,
  onClose,
}: AddPartnershipModalProps) {
  const [name, setName] = useState("");
  const [type, setType] = useState<string>("");
  const [adminEmail, setAdminEmail] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    type?: string;
    adminEmail?: string;
  }>({});
  const { toast } = useToast();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: { name?: string; type?: string; adminEmail?: string } = {};

    if (!name.trim()) {
      newErrors.name = "Partnership name is required";
    }

    if (!type) {
      newErrors.type = "Partnership type is required";
    }

    if (!adminEmail.trim()) {
      newErrors.adminEmail = "Administrator email address is required";
    } else if (!validateEmail(adminEmail.trim())) {
      newErrors.adminEmail = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB.",
        variant: "destructive",
      });
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      logger.log("🤝 Creating new partnership:", {
        name: name.trim(),
        type,
        adminEmail: adminEmail.trim(),
      });

      let logoUrl = "";

      if (logoFile) {
        logoUrl = await uploadPartnershipLogo(logoFile);
        logger.log("✅ Logo uploaded:", logoUrl);
      }

      const partnershipId = await addPartnership({
        name: name.trim(),
        type,
        logoUrl,
        enabled: true,
        created: Timestamp.now(),
      });
      logger.log("✅ Partnership record created with ID:", partnershipId);

      const invitationId = await addPartnershipUserInvitation({
        partnershipId,
        email: adminEmail.trim(),
        role: "administrator",
        status: "pending",
        inviteDate: null,
        error: "",
        sent: null,
        created: Timestamp.now(),
      });
      logger.log("✅ Partnership invitation record created with ID:", invitationId);

      toast({
        title: "Partnership created successfully",
        description: `${name.trim()} has been added and an invitation sent to ${adminEmail.trim()}.`,
      });

      setName("");
      setType("");
      setAdminEmail("");
      setLogoFile(null);
      setLogoPreview("");
      setErrors({});
      onClose();
    } catch (error: any) {
      logger.error("❌ Error creating partnership:", error);
      toast({
        title: "Failed to create partnership",
        description:
          error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName("");
      setType("");
      setAdminEmail("");
      setLogoFile(null);
      setLogoPreview("");
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Handshake className="h-5 w-5" />
            Add New Partnership
          </DialogTitle>
          <DialogDescription>
            Create a new partner or reseller relationship. An invitation will be sent to the administrator.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Partnership Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Enter partnership name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
              data-testid="input-partnership-name"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">
              Type <span className="text-red-500">*</span>
            </Label>
            <Select value={type} onValueChange={setType} disabled={isSubmitting}>
              <SelectTrigger data-testid="select-partnership-type">
                <SelectValue placeholder="Select partnership type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Partner">Partner</SelectItem>
                <SelectItem value="Reseller">Reseller</SelectItem>
              </SelectContent>
            </Select>
            {errors.type && (
              <p className="text-sm text-red-500">{errors.type}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-start space-x-4">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-20 h-20 object-contain rounded border"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-100 hover:bg-red-200"
                    onClick={handleRemoveLogo}
                    disabled={isSubmitting}
                  >
                    <X className="w-3 h-3 text-red-600" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50">
                  <Upload className="w-6 h-6 text-slate-400" />
                  <span className="text-xs text-slate-500 mt-1">Upload</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />
                </label>
              )}
              <div className="text-sm text-slate-500">
                <p>Upload a logo for the partnership.</p>
                <p>Max size: 5MB</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adminEmail">
              <Mail className="h-4 w-4 inline mr-1" />
              Administrator Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="adminEmail"
              type="email"
              placeholder="admin@partner.com"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              disabled={isSubmitting}
              data-testid="input-admin-email"
            />
            {errors.adminEmail && (
              <p className="text-sm text-red-500">{errors.adminEmail}</p>
            )}
            <p className="text-sm text-slate-500">
              An invitation will be sent to this email address.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Partnership"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
